import { useCallback, useEffect, useMemo, useRef } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PersonalSourceView } from "@/api/client/personal-sources"
import type { ChatSessionModel } from "@/api/models"

import { getSessionApi } from "@/api/api"
import { setSessionPersonalSearch } from "@/api/client/personal-sources"
import { usePersonalSources } from "@/features/personalization/hooks/use-personal-sources"
import { SettingsService } from "@/services/settings-service"

import {
  personalSearchAvailable,
  type PersonalSearchSession,
  personalSearchSessionState,
  searchablePersonalSources,
} from "../utils/personal-search-utils"

/** The slice of the Explorer store this hook reads and writes. */
type PersonalSearchStore = {
  (selector: (state: { personalSearchEnabled: boolean }) => boolean): boolean
  getState: () => {
    personalSearchEnabled: boolean
    setPersonalSearchEnabled: (enabled: boolean) => void
  }
}

export type PersonalSearchControl = {
  /** At least one of the user's sources is switched on for them: the row is shown. */
  available: boolean
  /** What the switch shows (and what a turn sends, see `personalSearchRequestFlag`). */
  enabled: boolean
  /** Forced off: someone else can see this chat, so it cannot be turned on here. */
  locked: boolean
  /** Personal content is already part of this chat. */
  tainted: boolean
  /** The sources that would be searched, for the status rows under the switch. */
  sources: PersonalSourceView[]
  isPending: boolean
  onChange: (enabled: boolean) => void
}

const sessionQueryKey = (sessionId: string) => ["personal-search-session", sessionId] as const

function readToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

/**
 * Personal search for the open chat (PS-5). The server owns the choice and stores it on the
 * session, so it persists across follow-up turns and reloads; this hook loads it when the chat
 * changes, mirrors it into the Explorer store for the turn request, and writes changes back.
 * A new chat starts on.
 */
export function usePersonalSearch(
  sessionId: string | null | undefined,
  currentUserOid: string | null | undefined,
  explorerStore: PersonalSearchStore
): PersonalSearchControl {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const sources = usePersonalSources()
  const available = personalSearchAvailable(sources.data)
  const storeEnabled = explorerStore((state) => state.personalSearchEnabled)
  // Read at error time. The mutation closure still holds the chat that started the request.
  const currentSessionIdRef = useRef(sessionId)
  currentSessionIdRef.current = sessionId

  const sessionQuery = useQuery({
    queryKey: sessionQueryKey(sessionId ?? ""),
    queryFn: () => {
      if (!sessionId) throw new Error("session id is required")
      return getSessionApi(sessionId, readToken())
    },
    enabled: available && typeof sessionId === "string" && sessionId.length > 0,
    retry: false,
    staleTime: 15_000,
  })

  const sessionState = useMemo(
    () =>
      personalSearchSessionState(
        sessionQuery.data as PersonalSearchSession | null | undefined,
        currentUserOid
      ),
    [sessionQuery.data, currentUserOid]
  )

  // Load the stored choice whenever the chat changes; a chat the server has no answer for starts on.
  // Until this session's read succeeds, keep the default on. An explicit false would persist onto
  // the new chat, and a failed load (retry: false) must not keep the previous chat's off flag.
  useEffect(() => {
    const { setPersonalSearchEnabled } = explorerStore.getState()
    if (!sessionId || !sessionQuery.isSuccess) {
      setPersonalSearchEnabled(true)
      return
    }
    setPersonalSearchEnabled(sessionState.enabled)
  }, [explorerStore, sessionId, sessionQuery.isSuccess, sessionState.enabled])

  const mutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setSessionPersonalSearch(id, enabled),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData<ChatSessionModel>(sessionQueryKey(id), (previous) =>
        previous
          ? {
              ...previous,
              personalSearch: { enabled: result.enabled, updatedAt: result.updatedAt },
            }
          : previous
      )
    },
    onError: (_error, { id, enabled }) => {
      // Put the switch back only if this chat is still open. A failure for the
      // previous chat must not write `!enabled` onto the one the user moved to.
      if (currentSessionIdRef.current === id) {
        explorerStore.getState().setPersonalSearchEnabled(!enabled)
      }
      toast.error(
        t("personalSources.search.toggleFailed", {
          defaultValue: "Could not change personal search for this chat. Please try again.",
        })
      )
    },
  })

  const onChange = useCallback(
    (enabled: boolean) => {
      if (sessionState.locked && enabled) return
      // One write at a time. A second click would let an older boolean finish last.
      if (mutation.isPending) return
      explorerStore.getState().setPersonalSearchEnabled(enabled)
      if (sessionId) mutation.mutate({ id: sessionId, enabled })
    },
    [explorerStore, mutation, sessionId, sessionState.locked]
  )

  return {
    available,
    enabled: sessionState.locked ? false : storeEnabled,
    locked: sessionState.locked,
    tainted: sessionState.tainted,
    sources: searchablePersonalSources(sources.data),
    isPending: mutation.isPending,
    onChange,
  }
}
